/**
 * "New group" — V6-05 §1.
 *
 * Pick members, then name it — in that order, because the name usually
 * follows from who is in it. Three people minimum: two already have a DM, and
 * a "group" of two would be a second parallel history for the same pair.
 */

import { useState } from 'react';
import { Loader2, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { UserAvatar } from '@/components/ui/avatar';
import { ApiError } from '@/lib/api';
import { useChatPeople, useCreateGroup, type ConversationView } from './api';

export function NewGroupDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (conversation: ConversationView) => void;
}) {
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<{ _id: string; name: string; avatarUrl?: string }[]>([]);
  const [name, setName] = useState('');
  const { data: people = [] } = useChatPeople(search);
  const create = useCreateGroup();

  // The creator counts toward the three, and is not in this list to pick.
  const enough = picked.length >= 2;

  async function submit(): Promise<void> {
    try {
      const conversation = (await create.mutateAsync({
        name: name.trim(),
        userIds: picked.map((person) => person._id),
      })) as ConversationView;
      toast.success(`“${conversation.name}” created`);
      onCreated(conversation);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not create the group');
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="New group"
      data-testid="new-group-dialog"
      className="fixed inset-0 z-50 grid place-items-center bg-black/30 p-4"
      onClick={onClose}
    >
      <div
        className="vt-surface vt-elevated flex w-[360px] flex-col gap-3 p-3"
        onClick={(event) => event.stopPropagation()}
      >
        <h3 className="text-[13px] font-medium">New group</h3>

        <div className="flex flex-col gap-1">
          <Label htmlFor="group-name">Name</Label>
          <Input
            id="group-name"
            data-testid="group-name"
            placeholder="Design review"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </div>

        {picked.length > 0 && (
          <div className="flex flex-wrap gap-1" data-testid="group-picked">
            {picked.map((person) => (
              <span
                key={person._id}
                className="vt-surface flex items-center gap-1 px-1.5 py-0.5 text-[11px]"
                data-testid="group-picked-person"
              >
                {person.name}
                <button
                  type="button"
                  aria-label={`Remove ${person.name}`}
                  onClick={() => setPicked((current) => current.filter((p) => p._id !== person._id))}
                >
                  <X className="size-3" />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="flex items-center gap-1.5 border-b border-[var(--border)] pb-1">
          <Search className="size-3.5 shrink-0 vt-muted" />
          <Input
            className="h-7 border-0 bg-transparent px-0"
            placeholder="Add people…"
            aria-label="Search people to add"
            data-testid="group-people-search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        <div className="max-h-[200px] overflow-y-auto" data-testid="group-people-results">
          {people
            .filter((person) => !picked.some((p) => p._id === person._id))
            .map((person) => (
              <button
                key={person._id}
                type="button"
                data-testid="group-person"
                data-name={person.name}
                className="flex w-full items-center gap-2 px-1 py-1.5 text-left hover:bg-[var(--surface-2)]"
                onClick={() => {
                  setPicked((current) => [...current, person]);
                  setSearch('');
                }}
              >
                <UserAvatar id={person._id} name={person.name} src={person.avatarUrl} size={22} />
                <span className="truncate text-[13px]">{person.name}</span>
              </button>
            ))}
          {people.length === 0 && search.trim() && (
            <p className="p-2 text-[12px] vt-muted">Nobody matches that.</p>
          )}
        </div>

        {!enough && (
          <p className="text-[11px] vt-muted">
            Pick at least two people — with you that makes three. Two people already have a direct
            message.
          </p>
        )}

        <div className="flex gap-1.5">
          <Button
            variant="primary"
            size="sm"
            data-testid="group-create"
            disabled={!name.trim() || !enough || create.isPending}
            onClick={() => void submit()}
          >
            {create.isPending ? <Loader2 className="animate-spin" /> : null}
            Create group
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
